import React, { memo, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalDescription,
  Cross,
  Text,
  Button,
  Space,
  Conditional,
} from '@cashfree-intl/coherent';

import {
  getViewport,
  getItemFromLocalStorage,
  getDeviceId,
} from 'utils/common';

import { trackLoginEvent } from 'containers/Login/helper';
import Analytics from 'utils/analytics';
import EVENTS from 'constants/analytics';
import { basename } from 'store';

import CustomIcon from '../../../components/CustomIcons';

import {
  onboardingModalData,
  ACCOUNT_EXISTS,
  USER_DETAILS_COMPLETED,
} from '../constants';
import { AuthContext } from '../../../contexts/AuthContext';

const OnboardingModal = props => {
  const [isClosed, resetClosed] = useState(false);
  const navigate = useNavigate();
  const { userObj, latitude, longitude } = useContext(AuthContext);

  const {
    modalName,
    closeModal,
    googleLogin,
    navigateAsPerUserFlow,
    enableLoader,
    formName,
    modalData,
    onboardingActions,
    setSignupFirstStepData,
    updateFormStatus,
  } = props;

  const handleCloseModal = () => {
    const [viewPortWidth] = getViewport();
    if (viewPortWidth <= 767) {
      resetClosed(true);
      setTimeout(() => closeModal(), 250);
    } else closeModal();
  };

  const handleCreateAccount = async () => {
    handleCloseModal();
    setSignupFirstStepData({
      googleToken: getItemFromLocalStorage('googleToken'),
      deviceId: await getDeviceId(),
    });
    updateFormStatus(USER_DETAILS_COMPLETED);
    navigate(`${basename}signup`);
    Analytics.trackEvent(
      EVENTS.GOOGLE_LOGIN.CREATE_ACCOUNT_POPUP,
      null,
      'OB',
    )();
  };

  const loginToAccount = async () => {
    handleCloseModal();
    enableLoader();
    googleLogin({
      body: {
        googleToken: getItemFromLocalStorage('googleToken') || modalData.token,
        forceVerifyEmail: 'true',
        deviceId: await getDeviceId(),
        latitude,
        longitude,
      },
      navigate,
      actions: { onboardingActions },
      successCb: res => {
        userObj.login(res);
        navigateAsPerUserFlow(res.token);
        trackLoginEvent({
          loginType: 'google_login',
          status: 'success',
          latitude,
          longitude,
          locationPermissionGranted: !!latitude,
        });
      },
    });
  };

  const createNewAccount = async () => {
    handleCloseModal();
    enableLoader();
    googleLogin({
      body: {
        googleToken: getItemFromLocalStorage('googleToken') || modalData.token,
        forceLogin: 'true',
        forceVerifyEmail: 'true',
        deviceId: await getDeviceId(),
        latitude,
        longitude,
      },
      navigate,
      formName,
      actions: { onboardingActions },
      successCb: res => {
        userObj.login(res);
        navigateAsPerUserFlow(res.token);
        trackLoginEvent({
          loginType: 'google_login',
          status: 'success',
          latitude,
          longitude,
          locationPermissionGranted: !!latitude,
        });
      },
    });
  };

  const updatedDesc = str =>
    str.replace(/date|device/gi, matched => modalData[matched]);

  return (
    <Modal
      open
      className={isClosed ? 'onboarding-modal close' : 'onboarding-modal'}
    >
      <ModalContent>
        <ModalHeader>
          <Text variant="h16" className="w-600">
            {onboardingModalData[modalName].header}
            <Cross
              data-event-name="OnboardingModal_Icon_Close"
              data-ignore-inner-text="true"
              onClick={handleCloseModal}
            />
          </Text>
        </ModalHeader>
        <ModalDescription>
          <Space direction="column" alignItems="center">
            <CustomIcon
              componentName={onboardingModalData[modalName].icon}
              size={onboardingModalData[modalName].iconSize || null}
            />
            <Text variant="h20" className="mr-top-16">
              {onboardingModalData[modalName].header}
            </Text>
            <Text className="mr-top-8 text-center">
              <div className="text-light">
                {modalName !== ACCOUNT_EXISTS
                  ? onboardingModalData[modalName].description[0]
                  : updatedDesc(onboardingModalData[modalName].description[0])}
              </div>
              <Conditional
                if={modalName === ACCOUNT_EXISTS}
                className="mr-top-16 text-light"
              >
                <div className="mr-top-16 text-light">
                  {onboardingModalData[modalName].description[1]}
                </div>
              </Conditional>
            </Text>
          </Space>
          <Space direction="column">
            {modalName === ACCOUNT_EXISTS ? (
              <div className="account-exists">
                <Button
                  fluid
                  primary
                  className="mr-top-24 mr-btm-24"
                  onClick={loginToAccount}
                >
                  {onboardingModalData[modalName].buttonText[0]}
                </Button>
                <Button fluid link className="p-0" onClick={createNewAccount}>
                  {onboardingModalData[modalName].buttonText[1]}
                </Button>
              </div>
            ) : (
              <div className="mr-top-24 create-account">
                <Button link onClick={handleCloseModal}>
                  {onboardingModalData[modalName].buttonText[1]}
                </Button>
                <Button primary onClick={handleCreateAccount}>
                  {onboardingModalData[modalName].buttonText[0]}
                </Button>
              </div>
            )}
          </Space>
        </ModalDescription>
      </ModalContent>
    </Modal>
  );
};

OnboardingModal.propTypes = {
  modalName: PropTypes.string.isRequired,
  closeModal: PropTypes.func.isRequired,
  googleLogin: PropTypes.func.isRequired,
  navigateAsPerUserFlow: PropTypes.func.isRequired,
  enableLoader: PropTypes.func.isRequired,
  formName: PropTypes.string.isRequired,
  modalData: PropTypes.object.isRequired,
  onboardingActions: PropTypes.object.isRequired,
  setSignupFirstStepData: PropTypes.func.isRequired,
  updateFormStatus: PropTypes.func.isRequired,
};

export default memo(OnboardingModal);
